// src/context/brandVoice/fieldRules.js — which controls a field row shows. PURE.
//
// ⭐ Every visibility rule of a field row lives HERE, so no component re-derives one
// inline and the two can never drift. The rules come from the plan's decisions 3, 4, 9–13.
export const RETRYABLE_SKIPS = new Set(['CREDITS_EXHAUSTED', 'LOOKUP_FAILED', 'DEGRADED']);
const OWNER = { seeded: 'seeded', machine: 'machine', unlocked: 'unlocked', human: 'human' };

// ⭐ v186: the three "avoid" lists (`policy: 'bucket_d'`) are GUARDRAILS (FieldRow draws the guardrail line).
export const isGuardrail = (field) => field?.policy === 'bucket_d';

// ⭐ v196 (BE 571ac04 + 2ad8bdf): a personalization run NEVER writes the three "avoid" lists (the sector
// D-seed that used to append to them is gone) NOR `verified_claims` (they read `engine_writable: false`
// always; /document/lock|unlock on them are accepted and change nothing). So those fields get ONE fixed,
// non-interactive chip — "Non scritto dall'IA" — and ⛔ no Freeze / Unlock: a lock control there could only
// do nothing. It is NOT the `human` chip ("Non modificabile dall'IA" = frozen, unlockable).
export const isNeverAIWritten = (field) => isGuardrail(field) || field?.widget === 'claims';

// ⭐ Sheet 09: `unlock_effective: false` WINS over a row that still reads `unlocked` — the
// AI refuses this workspace's runs outright, so "Sbloccato" would promise an update that
// never comes. The other chips state past facts and stay.
export const ownerChipKey = (field, ctx = {}) => {
  if (isNeverAIWritten(field)) return 'neverAI';
  const key = OWNER[field?.state] || null;
  return key === 'unlocked' && ctx.unlockEffective === false ? null : key;
};

/** ctx: { dirty, busy, runLocked, unlockEffective, enHalfPresent, outcome } */
export const canFreeze = (field, ctx) => !isNeverAIWritten(field) && field.engine_writable === true;
// ⛔ Unlock is never an "undo": it is offered only on a frozen row, and only when the
// document says unlocking would have an effect at all.
export const canUnlock = (field, ctx) => !isNeverAIWritten(field) && field.state === 'human'
  && ctx.unlockEffective !== false;
export const actionsDisabled = (ctx) => !!(ctx.dirty || ctx.busy || ctx.runLocked);

function englishStale(field, ctx) {
  const skipped = ctx.outcome?.translation === 'skipped' && RETRYABLE_SKIPS.has(ctx.outcome.translation_reason);
  return field.translation_stale === true || skipped;
}

export function canTranslateNow(field, ctx) {
  return field.state === 'human' && field.policy === 'type_c' && field.widget !== 'claims'
    && englishStale(field, ctx) && ctx.enHalfPresent !== false
    && !ctx.dirty && !ctx.busy && !ctx.runLocked;
}

/** → i18n key under brandVoice.english.* or null. */
export function englishBadge(field, ctx) {
  if (ctx.enHalfPresent === false) return null;
  const reason = ctx.outcome?.translation === 'skipped' ? ctx.outcome.translation_reason : null;
  if (reason === 'CREDITS_EXHAUSTED' || reason === 'LOOKUP_FAILED') return 'credits';
  if (reason === 'DEGRADED') return 'retry';
  if (field.translation_stale === true) return field.policy === 'bucket_d' ? 'manual' : 'stale';
  return null;
}

/** → i18n key under brandVoice.hint.* or null. */
export function completenessHint(field) {
  if (field.verdict === 'THIN') return 'thin';
  if (field.genericness === 'generic') return 'generic';
  return null;
}
